import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiPrinter, FiMail, FiAlertCircle, FiTrendingUp, FiRadio, FiCheck } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import Stock from '../../../model/Stock';
import { AnalyticsResult } from '../../../model/AnalyticsResult';
import { ContactListSummary } from '../../../model/List';
import { startSetStock } from '../../../actions/CurrentStock';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { toast } from '../Toast';
import EmptyState from '../EmptyState';
import { displayName, initials } from '../Header';
import { HoldersPriceChart } from '../Analytics';
import { providers } from '../shareRegistry/AllHolders';
import { RegistryData, useRegistryData, num, pct, signed, holderCount, emailCount, topRows } from '../shareRegistry/reportsData';
import {
    SectionKey, PageKey, Trend, parseSections, reportPages, reportFileName, quarterRange, qtrLabel, isQuarter,
    trend, findResult, netNew, signUps, capitalise,
} from './analyticsExport';

// /analytics-report/{ticker:exchange}/{quarter}?s=overview,announcements,…[&print=1], opened in a new tab from
// Dashboard › Analytics › Export Board Report. Same pattern as shareRegistry/ReportView: no header (AppRouter
// headerlessRoutes), A4 sheets in the Figma page's own units (1240 × 1754) zoomed down, Print / Save as PDF via the
// browser. Figma 482:1902 (cover), 482:1934 (glance), 484:1562 (announcements), 484:1755 (investors).
// Read-only sources: GET /api/dashboard/analytics (key results, chart, email, announcements, GA4 visitor total),
// the share registry GETs in reportsData.ts (top holders, register listing) and GET /api/ContactList/Lists.
// Anything without a source shows "—" or "Not connected yet"; every sentence is built from those figures only.

const dash = '—';

interface Ctx {
    stock: Stock; data: AnalyticsResult; registry: RegistryData; subscribers: number | null; sections: SectionKey[];
    company: string; provider: string; preparedBy: string; label: string;
}

const Sheet: React.FC<{ ctx: Ctx; page: number; pages: number }> = ({ ctx, page, pages, children }) =>
    <article className="ar-sheet">
        <header className="ar-running">
            {ctx.stock.companyLogoUrl
                ? <img className="ar-logo" src={ctx.stock.companyLogoUrl} alt="" />
                : <span className="ar-logo ar-initials">{initials(ctx.company)}</span>}
            <strong>{ctx.company.toUpperCase()}</strong>
            <span className="ar-label">{ctx.label}</span>
        </header>
        <div className="ar-rule" />
        <div className="ar-content">{children}</div>
        <footer className="ar-foot">
            <span className="ar-lockup"><img src="/images/logo-dark.svg" alt="Investi" /><i /></span>
            <span className="ar-foot-t">Prepared by {ctx.preparedBy} using Investi · Registry data supplied by {ctx.provider} · Generated {moment().format('D MMMM YYYY')}</span>
            <strong>Page {page} of {pages}</strong>
        </footer>
    </article>;

const H2: React.FC<{ title: string }> = ({ title }) => <div className="ar-h2"><h2>{title}</h2><i /></div>;

const Missing: React.FC<{ text: string }> = ({ text }) => <div className="ar-missing"><FiAlertCircle /> {text}</div>;

const TREND_TEXT: Record<Trend, string> = { up: '▲ improving', down: '▼ down', steady: '— steady' };

const Cover: React.FC<{ ctx: Ctx }> = ({ ctx }) => <article className="ar-sheet ar-cover">
    <img className="ar-cover-bg" src="/images/login/brand-panel.jpg" alt="" />
    <div className="ar-cover-top">
        {ctx.stock.companyLogoUrl
            ? <img className="ar-cover-logo" src={ctx.stock.companyLogoUrl} alt="" />
            : <span className="ar-cover-logo ar-initials">{initials(ctx.company)}</span>}
        <strong>{ctx.company.toUpperCase()}</strong>
    </div>
    <div className="ar-cover-mid">
        <span className="ar-cover-badge">INVESTOR RELATIONS REPORT</span>
        <h1>The quarter<br />in numbers</h1>
        <p className="ar-cover-period">{quarterRange(ctx.data.quarter, ctx.data.quarter === ctx.data.quarters[0])}</p>
        <p className="ar-cover-sub">How investors engaged with {ctx.company} across the share registry, ASX announcements, email and the Website Hub.</p>
    </div>
    <div className="ar-cover-foot">
        <div>
            <strong>Prepared for the board of {ctx.company}</strong>
            <span>Prepared by {ctx.preparedBy} using Investi · Generated {moment().format('D MMMM YYYY')}</span>
            <span>Registry data supplied by {ctx.provider}. Share prices are ASX daily closes.</span>
        </div>
        <div className="ar-cover-built"><span>BUILT WITH</span><img src="/images/logo.svg" alt="Investi" /></div>
    </div>
</article>;

const Glance: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const { keyResults, chart } = ctx.data;
    // the written summary is put together from the key results, nothing else
    const sentences = keyResults.filter(x => x.value !== dash).map(x => `${capitalise(x.label)} was ${x.value} (${x.detail}).`);
    const rows = [keyResults.slice(0, 2), keyResults.slice(2, 4), keyResults.slice(4, 6)].filter(r => r.length);
    return <>
        <H2 title="The quarter at a glance" />
        <p className="ar-lead">{sentences.length ? sentences.join(' ') : 'No results were recorded for this quarter yet.'}</p>
        <div className="ar-krs">
            {rows.map((r, i) => <div className="ar-kr-row" key={i}>
                {r.map(x => {
                    const t = trend(x);
                    return <div className="ar-kr" key={x.label}>
                        <div className="ar-kr-v"><strong>{x.value}</strong>{t && <span className={`ar-trend ${t}`}>{TREND_TEXT[t]}</span>}</div>
                        <span className="ar-kr-l">{capitalise(x.label)}</span>
                        <span className="ar-kr-d">{x.detail}</span>
                    </div>;
                })}
            </div>)}
        </div>
        <div className="ar-chart">
            <div className="ar-chart-h">
                <strong>Shareholders vs share price</strong>
                <span>Registry count (navy) against the ASX closing price (cyan); amber dots mark price-sensitive announcements</span>
            </div>
            {chart.points.length > 1 ? <HoldersPriceChart chart={chart} height={300} print={true} /> : <Missing text="No holder counts or prices for this quarter yet." />}
        </div>
        {chart.insight && <div className="ar-insight"><span className="icon-tile"><FiTrendingUp /></span><p>{chart.insight}</p></div>}
    </>;
};

const Announcements: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const { announcements, email } = ctx.data;
    const has = (k: SectionKey) => ctx.sections.includes(k);
    const totalViews = announcements.reduce((s, a) => s + a.views, 0);
    const top = [...announcements].sort((a, b) => b.views - a.views)[0];
    return <>
        {has('announcements') && <>
            <H2 title="Which announcements got the most attention" />
            <p className="ar-lead">Views are all-time visits to the announcement on the Website Hub. Opens are people who opened the announcement email. “Next day” is the change from the last ASX close before the release to the first close after it.</p>
            {announcements.length === 0 ? <Missing text="No announcements were released this quarter." /> : <div className="ar-table ar-ann">
                <div className="ar-row ar-head"><span>Announcement</span><span>Date</span><span className="ar-num">Views</span><span className="ar-num">Opens</span><span className="ar-num">Open rate</span><span className="ar-num">Next day</span></div>
                {announcements.map((a, i) => <div className="ar-row" key={i}>
                    <span className="ar-name">{a.headline}</span>
                    <span className="ar-sec">{moment(a.date).format('D MMM')}</span>
                    <span className="ar-num ar-sec">{num(a.views)}</span>
                    <span className="ar-num ar-sec">{a.emailOpens === null ? dash : num(a.emailOpens)}</span>
                    <span className="ar-num ar-muted">{dash}</span>
                    <span className={`ar-num ar-strong ${a.priceNextDayPct === null || Math.abs(a.priceNextDayPct) < 0.05 ? 'ar-sec' : a.priceNextDayPct > 0 ? 'ar-up' : 'ar-down'}`}>
                        {a.priceNextDayPct === null ? dash : `${a.priceNextDayPct > 0 ? '+' : a.priceNextDayPct < 0 ? '−' : ''}${Math.abs(a.priceNextDayPct).toFixed(1)}%`}
                    </span>
                </div>)}
            </div>}
            {top && totalViews > 0 && <div className="ar-insight"><span className="icon-tile"><FiRadio /></span>
                <p>“{top.headline}” drew the most views ({num(top.views)}), {pct(top.views / totalViews * 100, 0)} of the views across the announcements above.</p>
            </div>}
            {announcements.length > 0 && <p className="ar-note">Open rate per announcement isn’t connected yet (recipient counts per send aren’t in the Analytics data).</p>}
        </>}
        {has('email') && <>
            <h3 className="ar-h3">Email engagement, by campaign</h3>
            {email.length === 0 ? <Missing text="No campaigns were sent this quarter." /> : <>
                <div className="ar-bars">
                    {email.map((x, i) => <div className="ar-bar" key={i}>
                        <div className="ar-bar-t"><span>{x.title}</span><em>{x.openedPct.toFixed(1)}% opened · {x.clickedPct.toFixed(1)}% clicked</em></div>
                        <div className="ar-track"><span className="ar-opened" style={{ width: `${x.openedPct}%` }} /><span className="ar-clicked" style={{ width: `${x.clickedPct}%` }} /></div>
                    </div>)}
                </div>
                <div className="ar-legend"><span><i className="ar-dot-navy" /> Opened</span><span><i className="ar-dot-cyan" /> Clicked</span></div>
            </>}
        </>}
    </>;
};

const ptsText = (p: number | null) => {
    if (p === null) return <span className="ar-muted">{dash}</span>;
    if (Math.abs(p) < 0.05) return <span className="ar-muted">no change</span>;
    return <span className={p > 0 ? 'ar-up' : 'ar-down'}>{signed(p, 1)} pts</span>;
};

const Investors: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const { stock, registry, data } = ctx;
    const has = (k: SectionKey) => ctx.sections.includes(k);
    const rows = topRows(stock, registry, 20);
    const top5 = rows.slice(0, 5);
    const top20 = rows.reduce((s, r) => s + r.pct, 0);
    const top20Pts = rows.some(r => r.pts !== null) ? rows.reduce((s, r) => s + (r.pts ?? 0), 0) : null;
    const holders = holderCount(stock, registry.listing);
    const emails = emailCount(stock, registry.listing);
    const noEmail = holders !== null && emails !== null ? holders - emails : null;
    const registryResult = findResult(data.keyResults, 'registry growth');
    const visitorsResult = findResult(data.keyResults, 'Website Hub visitors');
    const net = netNew(registryResult);
    const ups = signUps(visitorsResult);

    // suggestions only from the figures on these pages
    const topAnn = [...data.announcements].sort((a, b) => b.views - a.views)[0];
    const notEmailed = data.announcements.filter(a => a.emailOpens === null).length;
    const falling = data.keyResults.find(x => trend(x) === 'down');
    const suggestions = [
        topAnn && topAnn.views > 0 && { t: `Keep releases like “${topAnn.headline}” coming.`, d: `It drew the most views of the quarter’s announcements (${num(topAnn.views)}).` },
        notEmailed > 0 && { t: 'Email the announcements that matter.', d: `${notEmailed} of the ${data.announcements.length} most-viewed announcements this quarter weren’t emailed to subscribers.` },
        noEmail !== null && noEmail > 0 && { t: `Ask ${num(noEmail)} holders for an email address.`, d: 'They have no email on the register, so every notice to them goes by post.' },
        falling && { t: `Look into ${falling.label}.`, d: `${falling.value} this quarter — ${falling.detail}.` },
    ].filter(Boolean).slice(0, 4) as { t: string; d: string }[];

    return <>
        <H2 title="Investors, website and what to do next" />
        {has('investors') && <>
            <h3 className="ar-h3">Who owns the company</h3>
            {!registry.top ? <Missing text="The top holders report isn’t available for this company yet." /> : <div className="ar-table ar-holders">
                <div className="ar-row ar-head"><span>Holder</span><span className="ar-num">Shares</span><span className="ar-num">% Held</span><span className="ar-num">Change · last 30 days</span></div>
                {top5.map(r => <div className="ar-row" key={`${r.position}:${r.name}`}>
                    <span className="ar-name">{r.name}</span><span className="ar-num ar-sec">{num(r.holding)}</span>
                    <span className="ar-num ar-sec">{pct(r.pct, 1)}</span><span className="ar-num">{ptsText(r.pts)}</span>
                </div>)}
            </div>}
            <div className="ar-kpis">
                <div className="ar-kpi"><strong>{holders === null ? dash : num(holders)}</strong><b>shareholders</b><span>{net ? `${net} net new this quarter` : 'on the register today'}</span></div>
                <div className="ar-kpi"><strong>{rows.length ? pct(top20, 1) : dash}</strong><b>held by the top 20</b><span>{top20Pts === null ? 'today' : `${signed(top20Pts, 1)} pts in 30 days`}</span></div>
                <div className="ar-kpi"><strong>{ups ?? dash}</strong><b>new sign‑ups</b><span>this quarter · matching to the register not connected yet</span></div>
                <div className="ar-kpi"><strong>{ctx.subscribers === null ? dash : num(ctx.subscribers)}</strong><b>email subscribers</b><span>on the default list today</span></div>
            </div>
        </>}
        {has('traffic') && <>
            <h3 className="ar-h3">Where investor‑centre visitors came from</h3>
            {data.sources.visits === null ? <Missing text="Google Analytics isn’t connected for this company." /> : <div className="ar-table ar-sources">
                <div className="ar-row ar-head"><span>Source</span><span className="ar-num">Visits</span><span className="ar-num">% of visits</span></div>
                {data.sources.items.map(x => <div className="ar-row" key={x.name}>
                    <span className="ar-name">{x.name}</span><span className="ar-num">{num(x.visits)}</span><span className="ar-num ar-sec">{x.pct}%</span>
                </div>)}
            </div>}
        </>}
        {suggestions.length > 0 && <div className="ar-next">
            <h3>What we suggest for next quarter</h3>
            {suggestions.map(s => <div className="ar-next-item" key={s.t}><FiCheck /><div><strong>{s.t}</strong><span>{s.d}</span></div></div>)}
        </div>}
    </>;
};

const AnalyticsReportView: React.FC = () => {
    const dispatch = useDispatch();
    const { ticker, quarter } = useParams<{ ticker: string; quarter: string }>();
    const location = useLocation();
    const user = useSelector((state: IGlobalState) => state.user);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const loaded = !!stock && `${stock.ticker}:${stock.exchange}` === ticker;
    const registry = useRegistryData(loaded ? stock : null);
    const [data, setData] = useState<AnalyticsResult | null>(null);
    const [subscribers, setSubscribers] = useState<number | null>(null);
    const [error, setError] = useState('');
    const printed = useRef(false);
    const search = new URLSearchParams(location.search);
    const sections = parseSections(location.search);
    const autoPrint = search.get('print') === '1';

    // no header here to load the current client, so load the one in the url
    useEffect(() => {
        if (!loaded) dispatch(startSetStock(ticker));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticker]);

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!loaded || !stock || !isQuarter(quarter)) return;
        const [[err, result], [, lists]] = await Promise.all([
            to(api().getAnalytics(stock.id, quarter)),
            to(api().getContactLists(stock.id)),
        ]);
        if (!isMounted()) return;
        if (err || !result) { setError((err as Error | null)?.message || 'Failed to load the analytics'); return; }
        setData(result as AnalyticsResult);
        const defaultList = ((lists as ContactListSummary[] | undefined) ?? []).find(l => !l.name);
        setSubscribers(defaultList ? defaultList.numSubscribers : null);
    }, [loaded, stock?.id, quarter]);

    const ready = !!data && !registry.loading;

    // "Download PDF" opens this page with print=1: print once everything (chart included, animation off) has rendered
    useEffect(() => {
        if (!ready || !autoPrint || printed.current) return;
        printed.current = true;
        const t = window.setTimeout(() => window.print(), 400);
        return () => window.clearTimeout(t);
    }, [ready, autoPrint]);

    if (!loaded) return null;

    if (!isQuarter(quarter))
        return <div className="v2-page analytics-report-view">
            <EmptyState art="caughtUp" title="Report not found" sub="Make it again from Dashboard › Analytics › Export Board Report." />
        </div>;

    const company = stock.companyName || stock.ticker;
    const providerName = providers[stock.shareRegistryProvider ?? 0];
    const pages = reportPages(sections);
    const ctx: Ctx | null = data && {
        stock, data, registry, subscribers, sections, company,
        provider: providerName && providerName !== 'None' ? providerName : 'the share registry',
        preparedBy: displayName(user),
        label: `INVESTOR REPORT · ${qtrLabel(data.quarter).toUpperCase()} ${data.quarter.slice(0, 4)}`,
    };

    const sheet = (key: PageKey, i: number) => {
        if (!ctx) return null;
        if (key === 'cover') return <Cover ctx={ctx} key={key} />;
        const body = key === 'glance' ? <Glance ctx={ctx} /> : key === 'announcements' ? <Announcements ctx={ctx} /> : <Investors ctx={ctx} />;
        return <Sheet ctx={ctx} page={i + 1} pages={pages.length} key={key}>{body}</Sheet>;
    };

    return <div className="v2-page analytics-report-view">
        <div className="ar-toolbar">
            <span className="ar-file">{reportFileName(stock, quarter)}</span>
            <button type="button" className="btn-outline" onClick={() => toast.info('Preview only: emailing the report isn’t connected yet — download the PDF and attach it for now.')}><FiMail /> Email This Report</button>
            <button type="button" className="btn-primary" onClick={() => window.print()} disabled={!ready}><FiPrinter /> Print / Save as PDF</button>
        </div>
        {!ready && !error && <p className="ar-loading">Loading the analytics and the register… (first load can take a minute while reports generate)</p>}
        {error && <p className="error">{error}</p>}
        {ready && <div className="ar-doc">{pages.map(sheet)}</div>}
    </div>;
};

export default AnalyticsReportView;
